import React, { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowRight, Compass, CreditCard, FileCheck, Download } from "lucide-react";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { usePrefillArea } from "@/hooks/use-contact-prefill";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const SceneE = React.lazy(() => import("@/components/scenes/SceneE"));

const TX_ICONS = [Compass, CreditCard, FileCheck, Download];

function useMotionValueState(mv, initial = 0) {
  const [value, setValue] = useState(initial);
  useMotionValueEvent(mv, "change", (v) => setValue(v));
  return value;
}

function SceneFallback() {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, background: "var(--surface-base)" }} />
  );
}

function parseUnitParam(search) {
  const p = new URLSearchParams(search);
  const raw = p.get("unit");
  if (raw === null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const i = Math.floor(n);
  if (i < 0 || i > 7) return null;
  return i;
}

function Hero() {
  const { t } = useT("platformData");
  const locale = useLocale();
  return (
    <section
      className="container-frac pt-24 pb-16 md:pt-32 md:pb-24"
      data-testid="data-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="data-cta-primary"
            >
              {t("hero.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CopyProblem() {
  const { t } = useT("platformData");
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="data-copy-problem"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("copyProblem.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("copyProblem.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("copyProblem.p1")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("copyProblem.p2")}
          </p>
        </Reveal>
        <Reveal delay={550}>
          <p className="mt-12 text-2xl font-bold leading-snug text-ink-primary sm:text-3xl">
            {t("copyProblem.closing")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function DataUnitTable() {
  const { t } = useT("platformData");
  const rows = t("unitTable.rows", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="data-unit-spec"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("unitTable.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("unitTable.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("unitTable.body")}
          </p>
        </Reveal>
      </div>

      <Reveal delay={350}>
        <Card
          variant="glass"
          className="mt-10 overflow-hidden p-0"
          data-testid="data-unit-table"
        >
          <table className="w-full text-left">
            <thead className="border-b border-hairline bg-surface-elevated/40">
              <tr>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-caps text-ink-tertiary">
                  {t("unitTable.colField")}
                </th>
                <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-caps text-ink-tertiary">
                  {t("unitTable.colExample")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-hairline last:border-b-0">
                  <td className="px-6 py-4 font-mono text-sm text-frac-violet">
                    {r.field}
                  </td>
                  <td className="px-6 py-4 text-sm text-ink-primary">
                    {r.example}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </Reveal>
    </section>
  );
}

function MintingSequence() {
  const ref = useRef(null);
  const location = useLocation();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);
  const { t } = useT("platformData");

  const initialUnit = useMemo(() => {
    const parsed = parseUnitParam(location.search);
    return parsed ?? 3;
  }, [location.search]);

  const handleUnitHighlight = useCallback((n) => {
    const params = new URLSearchParams(window.location.search);
    params.set("unit", String(n));
    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  }, []);

  return (
    <section
      ref={ref}
      className="relative"
      data-testid="data-minting"
    >
      <div className="relative min-h-[240vh]">
        <div className="sticky top-0 flex min-h-screen flex-col items-center justify-center py-16">
          <div className="container-frac">
            <div className="mx-auto max-w-3xl text-center">
              <Reveal>
                <span className="eyebrow">{t("minting.eyebrow")}</span>
              </Reveal>
              <MaskReveal
                as="h2"
                className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
              >
                {t("minting.h2")}
              </MaskReveal>
              <Reveal delay={200}>
                <p className="mt-8 text-lg leading-normal text-ink-secondary">
                  {t("minting.body")}
                </p>
              </Reveal>
            </div>
            <div
              className="relative mx-auto mt-10 h-[50vh] w-full max-w-6xl overflow-hidden rounded-[16px] border border-hairline"
              data-testid="data-scene-e-wrapper"
            >
              <Suspense fallback={<SceneFallback />}>
                <SceneE
                  scrollProgress={progress}
                  defaultUnit={initialUnit}
                  onUnitHighlight={handleUnitHighlight}
                  style={{ position: "absolute", inset: 0 }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProofWithoutExposure() {
  const { t } = useT("platformData");
  const onChainRows = t("onchain.onChainRows", { returnObjects: true }) || [];
  const neverList = t("onchain.neverOnChain", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="data-onchain"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("onchain.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("onchain.h2")}
        </MaskReveal>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
        <Reveal>
          <Card
            variant="glass"
            className="h-full"
            data-testid="data-onchain-panel"
          >
            <div className="text-[11px] font-semibold uppercase tracking-caps text-eyebrow">
              {t("onchain.onChainLabel")}
            </div>
            <ul className="mt-4 space-y-3">
              {onChainRows.map((r, i) => (
                <li key={i} className="border-b border-hairline pb-3 last:border-b-0 last:pb-0">
                  <div className="font-mono text-sm text-ink-primary">
                    {r.field}
                  </div>
                  <div className="mt-1 text-xs text-ink-secondary">
                    {r.body}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </Reveal>
        <Reveal delay={150}>
          <div
            className="relative h-full rounded-[16px] border border-hairline bg-surface-raised/40 p-6"
            data-testid="data-never-onchain-panel"
          >
            <div
              className="absolute inset-x-0 top-0 h-[2px] rounded-t-[16px]"
              style={{ background: "rgba(220, 60, 60, 0.5)" }}
              aria-hidden
            />
            <div className="text-[11px] font-semibold uppercase tracking-caps text-ink-tertiary">
              {t("onchain.neverLabel")}
            </div>
            <ul className="mt-4 space-y-3">
              {neverList.map((item, i) => (
                <li
                  key={i}
                  className="border-b border-hairline pb-3 text-sm text-ink-primary last:border-b-0 last:pb-0"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function AntiDoubleCounting() {
  const { t } = useT("platformData");
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="data-anti-double"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("antiDouble.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("antiDouble.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("antiDouble.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <p className="mt-8 text-sm italic text-ink-tertiary">
            {t("antiDouble.note")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Transaction() {
  const { t } = useT("platformData");
  const steps = t("transaction.steps", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="data-transaction"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("transaction.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("transaction.h2")}
        </MaskReveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" gap={70}>
        {steps.map((s, i) => {
          const Icon = TX_ICONS[i];
          return (
            <StaggerItem key={i}>
              <Card variant="glass" className="h-full" data-testid={`data-tx-step-${i + 1}`}>
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-hairline bg-surface-elevated/60">
                    {Icon && <Icon className="h-4 w-4 text-frac-violet" aria-hidden />}
                  </div>
                  <div className="text-4xl font-light leading-none text-frac-violet" style={{ fontWeight: 300 }}>
                    {`0${i + 1}`}
                  </div>
                </div>
                <h3 className="mt-4 text-lg font-bold text-ink-primary">
                  {s.name}
                </h3>
                <p className="mt-2 text-sm leading-normal text-ink-secondary">
                  {s.body}
                </p>
              </Card>
            </StaggerItem>
          );
        })}
      </Stagger>

      <Reveal delay={400}>
        <p className="mx-auto mt-8 max-w-3xl text-center text-sm italic text-ink-tertiary">
          {t("transaction.note")}
        </p>
      </Reveal>
    </section>
  );
}

function Buyers() {
  const { t } = useT("platformData");
  const items = t("buyers.items", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="data-buyers"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("buyers.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("buyers.h2")}
        </MaskReveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" gap={70}>
        {items.map((b, i) => (
          <StaggerItem key={i}>
            <Card variant="glass" className="h-full" data-testid={`data-buyer-${i}`}>
              <h3 className="text-base font-bold text-ink-primary">{b.name}</h3>
              <p className="mt-3 text-sm leading-normal text-ink-secondary">
                {b.body}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function SettlementRails() {
  const { t } = useT("platformData");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="data-settlement"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("settlement.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("settlement.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("settlement.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/platform/tokenization", locale)}
              variant="secondary-on-dark"
              size="md"
              data-testid="data-settlement-cta"
            >
              {t("settlement.cta")}
              <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  const { t } = useT("platformData");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="data-final-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <MaskReveal
          as="h2"
          className="text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("finalCta.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("finalCta.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="data-final-cta-primary"
            >
              {t("finalCta.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function PlatformData() {
  usePrefillArea("data_tokenization");

  return (
    <>
      <PageSeo
        title="Data Tokenization — One Unit, One Owner, One Count"
        description="Structured behavioural intelligence minted into discrete, metered, licensable units with on-chain proof and zero double-counting. A serial number with a ledger behind it."
        path="/platform/data"
        image="/og/platform-data.png"
      />
      <div data-testid="page-platform-data">
        <Hero />
        <CopyProblem />
        <DataUnitTable />
        <MintingSequence />
        <ProofWithoutExposure />
        <AntiDoubleCounting />
        <Transaction />
        <Buyers />
        <SettlementRails />
        <FinalCta />
      </div>
    </>
  );
}
